/** @jsx jsx */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { jsx } from '@emotion/core';
import { TablePagination, useTheme } from '@material-ui/core';

import { ICustomTheme } from 'interfaces';
import { TablePaginationActions } from 'components';
import euroIcon from 'assets/icons/euro.svg';
import numbersIcon from 'assets/icons/nombres.svg';
import { TProductDetails } from 'interfaces/challenge.types';
import { IProductsProps } from '..';
import ProductThumbnail from '../ProductsThumbnail';
import Value from '../Value';
import {
  StyledDataContainer,
  StyledDataItem,
  StyledLeftContainer,
  StyledPagination,
  StyledProductContainer,
  StyledProductInfo,
  StyledProductsContainer,
  StyledUnitToDisplay,
} from './styles';

export const ProductsDetailsListLineRendering = ({
  rowsPerPage,
  withHeaderFieldsDescription,
  productDetailsList,
  isGoalMecanicByProduct,
  displayFinancialValues,
  displayGainValues,
  unitToDisplay,
}: IProductsProps): JSX.Element => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  const theme: ICustomTheme = useTheme();

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const isPaginationEnabled =
    rowsPerPage && productDetailsList.length > rowsPerPage;

  const productsInPage = useMemo(() => {
    return rowsPerPage && isPaginationEnabled
      ? productDetailsList.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      )
      : productDetailsList;
  }, [isPaginationEnabled, rowsPerPage, page, productDetailsList]);

  return (
    <StyledProductsContainer>
      {withHeaderFieldsDescription && (
        <StyledProductContainer
          backgroundColor={theme.colors.grey.light5}
          fontColor={theme.colors.grey.light1}
          isItalicStyleText
        >
          <StyledLeftContainer>
            {t('challenges.product.item')}
          </StyledLeftContainer>
          <StyledDataContainer>
            <StyledDataItem>{t('challenges.product.quantity')}</StyledDataItem>
            {displayFinancialValues && <StyledDataItem>Euros</StyledDataItem>}
            {displayGainValues && (
              <StyledDataItem>
                {t('challenges.declaration.profit')}
              </StyledDataItem>
            )}
          </StyledDataContainer>
        </StyledProductContainer>
      )}
      {productsInPage.map((product: TProductDetails) => {
        const thresholdText =
          product.threshold && product.threshold > 1
            ? `${product.reward} ${unitToDisplay}${t(
              'challenges.declaration.thresholdUnit',
            )} ${t('challenges.declaration.thresholdTitle', {
              threshold: product.threshold,
            })}`
            : `${product.reward} ${unitToDisplay}${t(
              'challenges.declaration.thresholdUnit',
            )}`;
        const coefficientText =
          product.coefficient &&
          `${product.coefficient} ${unitToDisplay}${t(
            'challenges.declaration.thresholdUnit',
          )}`;
        return (
          <StyledProductContainer key={product.id}>
            <StyledLeftContainer>
              <ProductThumbnail
                css={{ margin: 0, width: 100, height: 100, minWidth: 100 }}
                thumbnail={product.visual}
              />
              <StyledProductInfo>
                <div>{product.unit}</div>
                <div>
                  <strong>{product.brand}</strong>
                  {product.brand && <span> - </span>}
                  {product.productName}
                </div>
                <div>{product.productCode}</div>
                {isGoalMecanicByProduct ? (
                  <div>{thresholdText}</div>
                ) : (
                  <div>{coefficientText}</div>
                )}
              </StyledProductInfo>
            </StyledLeftContainer>
            <StyledDataContainer>
              {product.quantity !== null && (
                <StyledDataItem>
                  <Value
                    value={product.quantity}
                    imgProps={{ src: numbersIcon, alt: 'quantity icon' }}
                    textProps={{
                      color: 'secondary',
                      fontSize: 16,
                    }}
                  />
                </StyledDataItem>
              )}
              {displayFinancialValues && product.financialValue !== null && (
                <StyledDataItem>
                  <Value
                    value={product.financialValue}
                    imgProps={{ src: euroIcon, alt: 'euro icon' }}
                    textProps={{ color: 'primary' }}
                  />
                </StyledDataItem>
              )}
              {displayGainValues && isGoalMecanicByProduct && (
                <StyledDataItem>
                  <Value
                    value={product.productRewardValue}
                    textProps={{ color: theme.colors.paletteFromBO.main }}
                  />
                  <StyledUnitToDisplay>{unitToDisplay}</StyledUnitToDisplay>
                </StyledDataItem>
              )}
              {displayGainValues && !isGoalMecanicByProduct && (
                <StyledDataItem>
                  <Value
                    value={product.productResultValue}
                    textProps={{ color: theme.colors.paletteFromBO.main }}
                  />
                  <StyledUnitToDisplay>{unitToDisplay}</StyledUnitToDisplay>
                </StyledDataItem>
              )}
            </StyledDataContainer>
          </StyledProductContainer>
        );
      })}
      {rowsPerPage && isPaginationEnabled && (
        <StyledPagination>
          <TablePagination
            rowsPerPageOptions={[]}
            page={page}
            count={productDetailsList.length}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            ActionsComponent={TablePaginationActions}
          />
        </StyledPagination>
      )}
    </StyledProductsContainer>
  );
};
